import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PauseIcon from '@mui/icons-material/Pause';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useBlockOrRefuseRefund from '../../../../hooks/refunds/useBlockOrRefuseRefund';
import useRefundOrder from '../../../../hooks/refunds/useRefundOrder';
import useValidateRefund from '../../../../hooks/refunds/useValidateRefund';
import { setSnackBar } from '../../../../states/app/staticSlice.js';
import DialogBox from './DialogBox.jsx';

export const RefundStatusIcon = (props) => {
  const { status } = props;
  const getStatusIcon = (backgroundColor, icon) => (
    <Box
      sx={{
        backgroundColor,
        height: 20,
        width: 20,
        border: '1px solid',
        borderColor: backgroundColor,
        borderRadius: 1,
        padding: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'primary.contrastText',
      }}
    >
      {icon}
    </Box>
  );

  const renderStatus = (backgroundColor, icon) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {(status?.code === 'REFUNDFAILED' ||
        status?.code === 'REFUND_FAILED_BO' ||
        status?.code === 'PARTIAL_REFUND_FAILED') && (
        <WarningIcon size="small" color="error" />
      )}

      {(status?.code === 'REFUND_PENDING' ||
        status?.code === 'PARTIAL_REFUND_PENDING') && (
        <HourglassTopIcon size="small" color="warning" />
      )}

      {status?.code !== 'REFUNDFAILED' &&
        status?.code !== 'REFUND_FAILED_BO' &&
        status?.code !== 'PARTIAL_REFUND_FAILED' &&
        status?.code !== 'REFUND_PENDING' &&
        status?.code !== 'PARTIAL_REFUND_PENDING' &&
        getStatusIcon(backgroundColor, icon)}
      <Typography
        {...props}
        sx={[
          {
            color:
              status?.code === 'REFUNDFAILED' ||
              status?.code === 'REFUND_FAILED_BO' ||
              status?.code === 'PARTIAL_REFUND_FAILED'
                ? 'error.main'
                : '',
          },
          {
            color:
              status?.code === 'REFUND_PENDING' ||
              status?.code === 'PARTIAL_REFUND_PENDING'
                ? 'warning.main'
                : '',
          },
        ]}
      >
        {status?.name}
      </Typography>
    </Box>
  );

  switch (status?.code) {
    case 'VALIDATEDFI':
      return renderStatus('success.main', <DoneIcon size="small" />);
    case 'REFUNDEDFI':
      return renderStatus('success.main', <DoneIcon size="small" />);
    case 'REFUND_FAILED_BO':
    case 'PARTIAL_REFUND_FAILED':
    case 'NOT_REFUNDED':
    case 'REFUNDFAILED':
      return renderStatus('error.main', <WarningIcon size="small" />);
    case 'REFUSEDFI':
    case 'REFUSEDSC':
      return renderStatus('error.main', <CloseIcon size="small" />);
    case 'WAITINGFI':
      return renderStatus('warning.main', <PauseIcon size="small" />);
    case 'TOVALIDATEFI':
      return renderStatus('info.main', <MoreHorizIcon size="small" />);
    case 'PARTIAL_REFUND_PENDING':
    case 'REFUND_PENDING':
      return renderStatus('warning.main', <HourglassTopIcon size="small" />);
    case 'TOVALIDATESC':
    default:
      return renderStatus('secondary.main', <MoreHorizIcon size="small" />);
  }
};

const isValidToRefund = (refund) => {
  let isAllowedApi = true;
  let isAllowedStatus = true;
  const api = refund?.order?.contact?.store?.seller?.apiType?.code;
  const allowedToRefund = ['MIRAKL', 'HOORTRADE'];
  if (!allowedToRefund.includes(api)) {
    isAllowedApi = false;
  }
  if (
    refund?.status?.code !== 'TOVALIDATEFI' &&
    refund?.status?.code !== 'REFUNDFAILED'
  ) {
    isAllowedStatus = false;
  }
  console.log(refund?.amountToRefund, api);
  return isAllowedApi && isAllowedStatus;
};

export const ActionButton = ({ refund, profile }) => {
  const { validateRefund } = useValidateRefund();
  const { blockOrRefuseRefund } = useBlockOrRefuseRefund();
  const { refundOrder } = useRefundOrder();
  const anchorRef = useRef(null);
  const dispatch = useDispatch();

  // Mémorisation des actions pour éviter les recréations inutiles
  const REFUND_ACTIONS = useMemo(
    () => [
      {
        id: 'validate',
        label: 'Valider la demande',
        successMessage: 'Demande validée',
        errorMessage: 'Erreur lors de la validation de la demande',
        callback: validateRefund,
        requireDescription: false,
        color: 'success',
        icon: <DoneIcon />,
      },
      {
        id: 'refuse',
        label: 'Refuser la demande',
        successMessage: 'Demande refusée',
        errorMessage: 'Erreur lors du refus de la demande',
        callback: blockOrRefuseRefund,
        requireDescription: true,
        color: 'error',
        icon: <CloseIcon />,
        isRefuse: true,
      },
      {
        id: 'block',
        label: 'Bloquer la demande',
        successMessage: 'Demande bloquée',
        errorMessage: 'Erreur lors du blocage de la demande',
        callback: blockOrRefuseRefund,
        requireDescription: true,
        color: 'warning',
        icon: <PauseIcon />,
        isBlock: true,
      },
      {
        id: 'validateAndRefund',
        label: 'Valider et rembourser',
        successMessage:
          'Demande validée, remboursement en cours. (le remboursement peut prendre quelques minutes, merci de patienter)',
        errorMessage: 'Erreur lors de la validation et du remboursement.',
        callback: refundOrder,
        requireDescription: false,
        color: 'info',
        icon: <DoneIcon />,
        condition: (refund) => isValidToRefund(refund),
      },
    ],
    [validateRefund, blockOrRefuseRefund, refundOrder]
  );

  // Calcul de l'action par défaut basé sur le statut du refund
  const defaultAction = useMemo(() => {
    return isValidToRefund(refund) ? REFUND_ACTIONS[3] : REFUND_ACTIONS[0];
  }, [refund, REFUND_ACTIONS]);

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [refusedDescription, setRefusedDescription] = useState('');
  const [selectedAction, setSelectedAction] = useState(defaultAction);

  // Synchronisation de l'action sélectionnée quand refund change
  useEffect(() => {
    setSelectedAction(defaultAction);
  }, [refund]);

  const handleMenuItemClick = (newAction) => {
    setSelectedAction(newAction);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    console.log('handleClose');
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
    setRefusedDescription('');
  };

  const handleClick = () => {
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    dispatch(
      setSnackBar({
        open: false,
        message: '',
        color: '',
      })
    );
    setIsLoading(true);
    try {
      setTimeout(() => {}, 1000);

      const data = await selectedAction?.callback(
        refund?._id,
        profile,
        selectedAction?.isBlock,
        refusedDescription
      );
      console.log(data);
      console.log(selectedAction);
      if (!data) {
        throw new Error(`Error ${selectedAction?.id}`);
      }

      if (
        data?.status?.code === 'REFUND_PENDING' ||
        data?.status?.code === 'PARTIAL_REFUND_PENDING'
      ) {
        dispatch(
          setSnackBar({
            open: true,
            message:
              'Demande validée, remboursement en cours en cours de traitement sur les plateformes de paiement...',
            color: 'success',
          })
        );
      }

      if (
        data?.status?.code === 'REFUND_FAILED_BO' ||
        data?.status?.code === 'PARTIAL_REFUND_FAILED'
      ) {
        dispatch(
          setSnackBar({
            open: true,
            message:
              'Le remboursement a été refusé par les plateformes de paiement ou le backoffice.',
            color: 'error',
          })
        );
      } else {
        dispatch(
          setSnackBar({
            open: true,
            message: selectedAction?.successMessage,
            color: 'success',
          })
        );
      }
      setIsLoading(false);
      setIsOpen(false);
      setRefusedDescription('');
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setIsOpen(false);
      setRefusedDescription('');
      dispatch(
        setSnackBar({
          open: true,
          message: selectedAction?.errorMessage,
          color: 'error',
        })
      );
    }
  };

  return (
    <>
      <ButtonGroup
        variant="text"
        ref={anchorRef}
        sx={{ width: '100%' }}
        color="info"
      >
        <Button onClick={handleClick}>{selectedAction?.label}</Button>
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {REFUND_ACTIONS.filter(
                    (option) => !option.condition || option.condition(refund)
                  ).map((option) => (
                    <MenuItem
                      key={option?.id}
                      selected={option?.id === selectedAction?.id}
                      onClick={() => handleMenuItemClick(option)}
                    >
                      {option?.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <DialogBox
        isLoading={isLoading}
        refund={refund}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        action={selectedAction}
        refusedDescription={refusedDescription}
        setRefusedDescription={setRefusedDescription}
        handleSubmit={handleSubmit}
      />
    </>
  );
};
